import { useState, useEffect, useRef } from 'react';
import { usePark } from '../contexts/ParkContext';
import axios from 'axios';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Search,
  Receipt,
  Filter,
  Download,
  Upload,
  CheckSquare,
  Square
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gastos = () => {
  const { selectedPark, selectedYear } = usePark();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [configCategories, setConfigCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterMes, setFilterMes] = useState('');
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, expenseId: null, type: 'single' });

  const [form, setForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    proveedor: '',
    categoria: '',
    subcategoria: '',
    monto: '',
    mes_contable: new Date().getMonth() + 1,
    año_contable: selectedYear,
    descripcion: '',
    comprobante_url: '',
    tipo_cuota: 'ordinario'
  });

  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  // Get subcategories for selected category
  const getSubcategories = () => {
    const cat = configCategories.find(c => c.nombre === form.categoria);
    return cat?.subcategories || [];
  };

  useEffect(() => {
    fetchConfigCategories();
  }, []);

  useEffect(() => {
    if (selectedPark) {
      fetchData();
    }
  }, [selectedPark, selectedYear, filterCategoria, filterMes]);

  const fetchConfigCategories = async () => {
    try {
      const response = await axios.get(`${API}/config/budget-categories`);
      setConfigCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let expensesUrl = `${API}/expenses?park_id=${selectedPark.id}&año=${selectedYear}`;
      if (filterCategoria) expensesUrl += `&categoria=${filterCategoria}`;
      if (filterMes) expensesUrl += `&mes=${filterMes}`;

      const [expensesRes, categoriesRes] = await Promise.all([
        axios.get(expensesUrl),
        axios.get(`${API}/categories?park_id=${selectedPark.id}&año=${selectedYear}`)
      ]);
      
      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        park_id: selectedPark.id,
        monto: parseFloat(form.monto),
        mes_contable: parseInt(form.mes_contable),
        año_contable: parseInt(form.año_contable)
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, data);
        toast.success('Gasto actualizado');
      } else {
        await axios.post(`${API}/expenses`, data);
        toast.success('Gasto registrado');
      }
      
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar el gasto');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success('Gasto eliminado');
      setDeleteConfirm({ open: false, expenseId: null, type: 'single' });
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el gasto');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setForm({
      fecha: expense.fecha,
      proveedor: expense.proveedor,
      categoria: expense.categoria,
      subcategoria: expense.subcategoria || '',
      monto: expense.monto.toString(),
      mes_contable: expense.mes_contable,
      año_contable: expense.año_contable,
      descripcion: expense.descripcion || '',
      comprobante_url: expense.comprobante_url || '',
      tipo_cuota: expense.tipo_cuota || 'ordinario'
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setForm({
      fecha: new Date().toISOString().split('T')[0],
      proveedor: '',
      categoria: '',
      subcategoria: '',
      monto: '',
      mes_contable: new Date().getMonth() + 1,
      año_contable: selectedYear,
      descripcion: '',
      comprobante_url: '',
      tipo_cuota: 'ordinario'
    });
  };

  const downloadCSVTemplate = () => {
    const template = `fecha,proveedor,categoria,subcategoria,monto,mes_contable,año_contable,descripcion
2026-01-15,Seguridad Privada SA,Seguridad,Vigilancia,25000,1,2026,Servicio mensual de vigilancia
2026-01-20,CFE,Servicios,Energía eléctrica,8500,1,2026,Consumo enero áreas comunes
2026-01-22,Jardinería Verde,Jardinería,Mantenimiento,4500,1,2026,Poda y riego mensual
2026-02-01,Limpieza Industrial,Limpieza,Áreas comunes,6000,2,2026,Servicio de limpieza febrero
2026-02-10,Mantenimiento Total,Mantenimiento,Reparaciones,12000,2,2026,Reparación portón principal`;
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_gastos.csv';
    a.click();
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/expenses/import-csv?park_id=${selectedPark.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Se importaron ${response.data.imported} gastos`);
      if (response.data.errors.length > 0) {
        response.data.errors.slice(0, 5).forEach(err => toast.error(err));
        if (response.data.errors.length > 5) {
          toast.error(`...y ${response.data.errors.length - 5} errores más`);
        }
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
    }
    
    e.target.value = '';
  };

  // Selection functions
  const toggleSelectExpense = (expenseId) => {
    setSelectedExpenses(prev => 
      prev.includes(expenseId) 
        ? prev.filter(id => id !== expenseId)
        : [...prev, expenseId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedExpenses.length === expenses.length) {
      setSelectedExpenses([]);
    } else {
      setSelectedExpenses(expenses.map(e => e.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedExpenses.length === 0) return;
    
    setDeleting(true);
    let deleted = 0;
    let errors = 0;
    
    for (const id of selectedExpenses) {
      try {
        await axios.delete(`${API}/expenses/${id}`);
        deleted++;
      } catch (error) {
        errors++;
      }
    }
    
    setDeleting(false);
    setSelectedExpenses([]);
    setDeleteConfirm({ open: false, expenseId: null, type: 'single' });
    
    if (deleted > 0) {
      toast.success(`Se eliminaron ${deleted} gastos`);
    }
    if (errors > 0) {
      toast.error(`${errors} gastos no pudieron eliminarse`);
    }
    
    fetchData();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value || 0);
  };

  const totalGastos = expenses.reduce((sum, e) => sum + (e.monto || 0), 0);

  // Group by category for summary
  const gastosPorCategoria = expenses.reduce((acc, e) => {
    acc[e.categoria] = (acc[e.categoria] || 0) + e.monto;
    return acc;
  }, {});

  if (!selectedPark) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Gastos</h1>
          <p>Selecciona un parque para ver los gastos</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="gastos-title">Gastos</h1>
        <p>{selectedPark?.nombre} - Año {selectedYear}</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card-industrial md:col-span-1">
          <span className="label-overline">Total Gastos</span>
          <p className="text-3xl font-black tracking-tight mt-2 tabular-nums text-red-600" data-testid="total-gastos">
            {formatCurrency(totalGastos)}
          </p>
          <p className="text-sm text-slate-500 mt-2">{expenses.length} registros</p>
        </div>
        <div className="card-industrial md:col-span-3">
          <span className="label-overline mb-4 block">Gastos por Categoría</span>
          <div className="flex flex-wrap gap-4">
            {Object.entries(gastosPorCategoria).map(([cat, monto]) => (
              <div key={cat} className="flex-1 min-w-[150px]">
                <p className="text-sm text-slate-500">{cat}</p>
                <p className="font-bold tabular-nums">{formatCurrency(monto)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="filter-bar justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-slate-500">
            <Filter size={18} />
            <span className="text-sm font-medium">Filtros:</span>
          </div>
          <Select value={filterCategoria || "all"} onValueChange={(v) => setFilterCategoria(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 input-industrial" data-testid="filter-categoria">
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {configCategories.map(cat => (
                <SelectItem key={cat.id} value={cat.nombre}>{cat.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterMes || "all"} onValueChange={(v) => setFilterMes(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 input-industrial" data-testid="filter-mes">
              <SelectValue placeholder="Mes contable" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {meses.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          {selectedExpenses.length > 0 && (
            <Button
              variant="destructive"
              className="rounded-none"
              onClick={() => setDeleteConfirm({ open: true, expenseId: null, type: 'multiple' })}
              disabled={deleting}
              data-testid="delete-selected-btn"
            >
              <Trash2 size={16} className="mr-2" />
              {deleting ? 'Eliminando...' : `Eliminar (${selectedExpenses.length})`}
            </Button>
          )}
          <Button
            variant="outline"
            className="rounded-none"
            onClick={downloadCSVTemplate}
            data-testid="download-expenses-template-btn"
          >
            <Download size={16} className="mr-2" />
            Plantilla CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
            className="hidden"
          />
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => fileInputRef.current?.click()}
            data-testid="import-expenses-csv-btn"
          >
            <Upload size={16} className="mr-2" />
            Importar CSV
          </Button>
          <Button 
            className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => { resetForm(); setShowModal(true); }}
            data-testid="add-expense-btn"
          >
            <Plus size={16} className="mr-2" />
            Nuevo Gasto
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="card-industrial animate-pulse">
          <div className="h-64 bg-slate-100"></div>
        </div>
      ) : expenses.length === 0 ? (
        <div className="empty-state py-16 card-industrial">
          <div className="empty-state-icon">
            <Receipt size={24} className="text-slate-400" />
          </div>
          <h3>No hay gastos registrados</h3>
          <p>Comienza registrando los gastos del parque.</p>
        </div>
      ) : (
        <div className="card-industrial overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="table-blueprint" data-testid="expenses-table">
              <thead>
                <tr>
                  <th className="w-12">
                    <Checkbox
                      checked={selectedExpenses.length === expenses.length && expenses.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-expenses"
                    />
                  </th>
                  <th>Fecha</th>
                  <th>Proveedor</th>
                  <th>Categoría</th>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th className="text-right">Monto</th>
                  <th>Mes Contable</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense, index) => (
                  <tr 
                    key={expense.id} 
                    className={`animate-slideIn ${selectedExpenses.includes(expense.id) ? 'bg-amber-50' : ''}`} 
                    style={{ animationDelay: `${index * 20}ms` }}
                  >
                    <td>
                      <Checkbox
                        checked={selectedExpenses.includes(expense.id)}
                        onCheckedChange={() => toggleSelectExpense(expense.id)}
                        data-testid={`select-expense-${expense.id}`}
                      />
                    </td>
                    <td className="font-mono text-sm">{expense.fecha}</td>
                    <td className="font-medium">{expense.proveedor}</td>
                    <td>
                      <span className="text-sm">{expense.categoria}</span>
                      {expense.subcategoria && (
                        <span className="text-slate-400 text-xs block">{expense.subcategoria}</span>
                      )}
                    </td>
                    <td>
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                        expense.tipo_cuota === 'extraordinario' 
                          ? 'bg-amber-100 text-amber-800' 
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {expense.tipo_cuota === 'extraordinario' ? 'Extra' : 'Ord.'}
                      </span>
                    </td>
                    <td className="text-slate-500 text-sm truncate max-w-xs">{expense.descripcion || '-'}</td>
                    <td className="text-right tabular-nums font-medium text-red-600">{formatCurrency(expense.monto)}</td>
                    <td>{meses.find(m => m.value === expense.mes_contable)?.label}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="p-2 hover:bg-slate-100 transition-colors"
                          data-testid={`edit-expense-${expense.id}`}
                        >
                          <Edit2 size={16} className="text-slate-500" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ open: true, expenseId: expense.id, type: 'single' })}
                          className="p-2 hover:bg-red-50 transition-colors"
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-medium">
                  <td></td>
                  <td colSpan={5}>Total</td>
                  <td className="text-right tabular-nums text-red-600">{formatCurrency(totalGastos)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">
              {editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveExpense}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Fecha</Label>
                  <Input
                    type="date"
                    value={form.fecha}
                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                    required
                    className="input-industrial"
                    data-testid="expense-fecha-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Monto (MXN)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.monto}
                    onChange={(e) => setForm({ ...form, monto: e.target.value })}
                    placeholder="0.00"
                    required
                    className="input-industrial"
                    data-testid="expense-monto-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Proveedor</Label>
                <Input
                  value={form.proveedor}
                  onChange={(e) => setForm({ ...form, proveedor: e.target.value })}
                  placeholder="Nombre del proveedor"
                  required
                  className="input-industrial"
                  data-testid="expense-proveedor-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Categoría</Label>
                  <Select
                    value={form.categoria}
                    onValueChange={(value) => setForm({ ...form, categoria: value, subcategoria: '' })}
                  >
                    <SelectTrigger className="input-industrial" data-testid="expense-categoria-select">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {configCategories.map(cat => (
                        <SelectItem key={cat.id} value={cat.nombre}>{cat.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Subcategoría</Label>
                  <Select
                    value={form.subcategoria || "none"}
                    onValueChange={(value) => setForm({ ...form, subcategoria: value === "none" ? "" : value })}
                    disabled={!form.categoria}
                  >
                    <SelectTrigger className="input-industrial" data-testid="expense-subcategoria-select">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin subcategoría</SelectItem>
                      {getSubcategories().map(sub => (
                        <SelectItem key={sub.id} value={sub.nombre}>{sub.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Mes Contable</Label>
                  <Select
                    value={form.mes_contable.toString()}
                    onValueChange={(value) => setForm({ ...form, mes_contable: parseInt(value) })}
                  >
                    <SelectTrigger className="input-industrial" data-testid="expense-mes-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {meses.map(m => (
                        <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Año Contable</Label>
                  <Input
                    type="number"
                    value={form.año_contable}
                    onChange={(e) => setForm({ ...form, año_contable: parseInt(e.target.value) })}
                    required
                    className="input-industrial"
                    data-testid="expense-year-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Descripción</Label>
                <Input
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Detalles del gasto..."
                  className="input-industrial"
                  data-testid="expense-descripcion-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Tipo de Gasto</Label>
                <Select
                  value={form.tipo_cuota}
                  onValueChange={(value) => setForm({ ...form, tipo_cuota: value })}
                >
                  <SelectTrigger className="input-industrial" data-testid="expense-tipo-cuota-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ordinario">Ordinario</SelectItem>
                    <SelectItem value="extraordinario">Extraordinario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-expense-btn">
                {editingExpense ? 'Guardar Cambios' : 'Registrar Gasto'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ ...deleteConfirm, open })}
        onConfirm={() => {
          if (deleteConfirm.type === 'multiple') {
            handleDeleteSelected();
          } else {
            handleDeleteExpense(deleteConfirm.expenseId);
          }
        }}
        title="¿Estás seguro que deseas eliminar?"
        description={deleteConfirm.type === 'multiple' 
          ? `Se eliminarán ${selectedExpenses.length} gastos permanentemente.`
          : "Este gasto será eliminado permanentemente."
        }
        confirmText="Eliminar"
      />
    </div>
  );
};

export default Gastos;
